/**
 * @module components/TabsTableHazard
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Tab display of various hazard table displays
 * 
 * The following assumes that user specified only contains one site designation
 */

import { useMemo } from 'react';

import { CSVLink } from 'react-csv';

import { useLanguageState } from '../contexts/language-state-provider';

import { calcInterpolatedSA, vs30toSiteDesignation } from '../lib';

import TableSiteDesignation from './table/table-site-designation';


type Props = {
  variables: HazardParameters
  data: SiteDesignationsXVResponse | SiteDesignationsXSResponse,
}

type GenericSiteResponse = SiteDesignationXVResponse | SiteDesignationXSResponse;


/**
 * React component
 * ===============
 */
const TabsTableEngineer = ({
  variables,
  data,
}: Props) => {
  const [ lang ] = useLanguageState();
  
  const prob2 = useMemo(() => (data.userSpecified as SiteDesignationXVResponse[]).find((
      v: GenericSiteResponse
    ) => v.poe50 === 2
  ), [data]);

  const prob5 = useMemo(() => (data.userSpecified as SiteDesignationXVResponse[]).find((
      v: GenericSiteResponse
    ) => v.poe50 === 5
  ), [data]);

  const prob10 = useMemo(() => (data.userSpecified as SiteDesignationXVResponse[]).find((
      v: GenericSiteResponse
    ) => v.poe50 === 10
  ), [data]);

  if( !prob2 || !prob5 || !prob10 ){
    console.error('Could not find the required probabilities')
    return null;
  }

  const interpSaProb2 = calcInterpolatedSA(
    2.0, prob2.sa2p0, 5.0, prob2.sa5p0, 4.0
  ).toFixed(4);

  const interpSaProb5 = calcInterpolatedSA(
    2.0, prob5.sa2p0, 5.0, prob5.sa5p0, 4.0
  ).toFixed(4);

  const interpSaProb10 = calcInterpolatedSA(
    2.0, prob10.sa2p0, 5.0, prob10.sa5p0, 4.0
  ).toFixed(4);

  // Convert code/model into a print friendly format
  const code = lang.codeMap[variables.code];

  // Create the text for the first element only
  const site = vs30toSiteDesignation(prob2.vs30)
  const printSite = <>{site.charAt(0)}<sub>{site.substring(1)}</sub></>
  
  // Get date and time values are generated
  let dateTime = new Date()

  // array of interpolation that length matches map below
  const mappedInterpolated = [interpSaProb2, interpSaProb5, interpSaProb10]
  // create the probability data that will be stored in the CSV
  const csvdata = [prob2, prob5, prob10].map((prob, idx) => {
    if (lang.abbr === 'fr') {
      return {
        'Code': variables.code,
        'Latitude': variables.latitude,
        'Longitude': variables.longitude,
        'Désignation d\'emplacement': site,
        'Probabilité (% en 50 ans)': prob.poe50,
        'Sa(0.2) [g]': prob.sa0p2,
        'Sa(0.5) [g]': prob.sa0p5,
        'Sa(1.0) [g]': prob.sa1p0,
        'Sa(2.0) [g]': prob.sa2p0,
        'Sa(5.0) [g]': prob.sa5p0,
        'Sa(10.0) [g]': prob.sa10p0,
        'PGA [g]': prob.pga,
        'PGV [m/s]': prob.pgv,
        'Log-log interpolé Sa(4.0) [g]': mappedInterpolated[idx],
        'Date de création': dateTime,
      }
    }
    return {
      'Code': variables.code,
      'Latitude': variables.latitude,
      'Longitude': variables.longitude,
      'Site Designation': site,
      'Probability (% in 50 years)': prob.poe50,
      'Sa(0.2) [g]': prob.sa0p2,
      'Sa(0.5) [g]': prob.sa0p5,
      'Sa(1.0) [g]': prob.sa1p0,
      'Sa(2.0) [g]': prob.sa2p0,
      'Sa(5.0) [g]': prob.sa5p0,
      'Sa(10.0) [g]': prob.sa10p0,
      'PGA [g]': prob.pga,
      'PGV [m/s]': prob.pgv,
      'Log-log interpolated Sa(4.0) [g]': mappedInterpolated[idx],
      'Date Generated': dateTime,
    }
  })
  
  let textProb2 = (
    <p>The log-log interpolated 2%/50 year S<sub>a</sub>(4.0, {printSite}) value is : <strong>{interpSaProb2}</strong></p>
  )
  if (lang.abbr === 'fr') {
    textProb2 = (
      <p>La valeur interpolée bi-logarithmique de 2 % en 50 ans de S<sub>a</sub>(4.0, {printSite}) est : <strong>{interpSaProb2}</strong></p>
    )
  }

  let textProb5 = (
    <p>The log-log interpolated 5%/50 year S<sub>a</sub>(4.0, {printSite}) value is : <strong>{interpSaProb5}</strong></p>
  )
  if (lang.abbr === 'fr') {
    textProb5 = (
      <p>La valeur interpolée bi-logarithmique de 5 % en 50 ans de S<sub>a</sub>(4.0, {printSite}) est : <strong>{interpSaProb5}</strong></p>
    )
  }

  let textProb10 = (
    <p>The log-log interpolated 10%/50 year S<sub>a</sub>(4.0, {printSite}) value is : <strong>{interpSaProb10}</strong></p>
  )
  if (lang.abbr === 'fr') {
    textProb10 = (
      <p>La valeur interpolée bi-logarithmique de 10 % en 50 ans de S<sub>a</sub>(4.0, {printSite}) est : <strong>{interpSaProb10}</strong></p>
    )
  }
  return (
    <>
      { (variables.code in lang.tab.nbcDetailMap) &&
        lang.tab.nbcDetailMap[variables.code]
      }
      <TableSiteDesignation
        caption={`${code} - ${lang.table.nbcTitle2}`}
        siteDesignation={prob2}
      />
      {textProb2}
      <details>
        <summary>{lang.tab.nbcCollapse}</summary>
        <TableSiteDesignation
          caption={`${code} - ${lang.table.nbcTitle5}`}
          siteDesignation={prob5}
        />
        {textProb5}
        <TableSiteDesignation
          caption={`${code} - ${lang.table.nbcTitle10}`}
          siteDesignation={prob10}
        />
        {textProb10}
      </details>
      <CSVLink
        data={csvdata}
        filename={`${variables.latitude}_${variables.longitude}_${variables.code}_${site}.csv`}
        className="btn btn-primary"
        target="_blank"
      >
        {lang.table.download}
      </CSVLink>
    </>
  );
};


export default TabsTableEngineer;